/*
 * signalr-core.js — tab coordination rules for PIRO WebPOS/CP SignalR notifications.
 *
 * Pure: no DOM, no Vue, no @microsoft/signalr. Everything environment specific comes in through `env`
 * (see createCore) so the rules can be unit tested with `node --test src/test`.
 * Browser glue lives in signalr-client.js, Nuxt wiring in plugins/signalr.js.
 * Ported from Fusion (PIRO.Fusion.v1/Resources/JScripts/signalr-core.js), simplified to the WebPOS
 * envelope: { notificationId, notificationType, transactionId, data } — no Form scopes.
 * Rules: PIRO.API.v3/Docs/signalR/developer-guide.md.
 */
((root, factory) => {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.PIROSignalRCore = factory();
  }
})(typeof self !== 'undefined' ? self : this, () => {
  'use strict';

  const DEFAULT_SETTINGS = {
    ownerWaitMs: 500,          // how long a non-owner tab waits for the owner to claim
    claimDelayVisibleMs: 150,  // visible but unfocused tab
    claimDelayHiddenMs: 300,   // hidden slave tab
    claimDelayMasterMs: 1000,  // hidden master: last resort, so a visible tab wins the prompt
    seenIdsMax: 200
  };

  const OUTCOMES = {
    handled: 'handled',                 // a subscriber's handle ran in this tab
    handledFallback: 'handled-fallback',// the generic fallback handler ran in this tab
    duplicate: 'duplicate',             // notificationId already processed by this tab
    lostClaim: 'lost-claim',            // another tab won the claim
    noHandler: 'no-handler',            // nobody handled it anywhere in this tab
    invalid: 'invalid'
  };

  function markerKey(notification) {
    return 'done.' + notification.notificationId;
  }

  function toArray(value) {
    if (value == null) return [];
    return Array.isArray(value) ? value : [value];
  }

  /**
   * @param {object} env
   *   isVisible(): bool, hasFocus(): bool, isMaster(): bool
   *   delay(ms): Promise<void>
   *   isClaimed(key): bool                - a tab already handled this notification (shared marker)
   *   tryClaim(key): Promise<bool>        - atomically claim; true for exactly one tab across the browser
   *   log(level, message, data?)          - level: 'debug' | 'warn' | 'error'
   * @param {object} settings - overrides of DEFAULT_SETTINGS
   */
  function createCore(env, settings) {
    const cfg = {};
    Object.keys(DEFAULT_SETTINGS).forEach(k => {
      const v = settings && settings[k];
      cfg[k] = (typeof v === 'number' && v >= 0) ? v : DEFAULT_SETTINGS[k];
    });

    const subscribers = [];
    let fallbackHandler = null;
    const outcomeListeners = [];
    const seenIds = [];
    const seenSet = Object.create(null);

    function log(level, message, data) {
      try { env.log(level, message, data); } catch (_) { /* logging never breaks delivery */ }
    }

    function remember(id) {
      if (seenSet[id]) return false;
      seenSet[id] = true;
      seenIds.push(id);
      while (seenIds.length > cfg.seenIdsMax) {
        delete seenSet[seenIds.shift()];
      }
      return true;
    }

    /**
     * @param {object} subscriber
     *   types: string | string[]      - notificationType values this subscriber serves
     *   claim?: (n) => bool           - does this tab own the notification (e.g. its transactionId
     *                                   matches the checkout this tab started)? A claiming subscriber
     *                                   runs at once and only in the owner tab.
     *   handle?: (n) => void          - runs in exactly one tab (owner, or the claim winner)
     *   everyTab?: (n) => void        - runs in every tab that receives the notification, before any claim
     * @returns {() => void} unsubscribe
     */
    function subscribe(subscriber) {
      if (!subscriber || typeof subscriber === 'function') {
        throw new Error('[signalr] subscribe expects an object { types, claim?, handle?, everyTab? }.');
      }
      const types = toArray(subscriber.types || subscriber.type);
      if (types.length === 0) {
        throw new Error('[signalr] subscribe: "types" is required.');
      }
      if (typeof subscriber.handle !== 'function' && typeof subscriber.everyTab !== 'function') {
        throw new Error('[signalr] subscribe: at least one of "handle" or "everyTab" is required.');
      }
      const entry = {
        types,
        claim: typeof subscriber.claim === 'function' ? subscriber.claim : null,
        handle: typeof subscriber.handle === 'function' ? subscriber.handle : null,
        everyTab: typeof subscriber.everyTab === 'function' ? subscriber.everyTab : null
      };
      subscribers.push(entry);
      return function unsubscribe() {
        const i = subscribers.indexOf(entry);
        if (i >= 0) subscribers.splice(i, 1);
      };
    }

    /** Generic handler used when a notification wins the user-level claim and no subscriber applies. */
    function setFallbackHandler(fn) {
      fallbackHandler = typeof fn === 'function' ? fn : null;
    }

    /** Diagnostics: fn({ outcome, id, type, ...extra }, notification) after every processed notification. */
    function onOutcome(fn) {
      if (typeof fn !== 'function') return () => {};
      outcomeListeners.push(fn);
      return () => {
        const i = outcomeListeners.indexOf(fn);
        if (i >= 0) outcomeListeners.splice(i, 1);
      };
    }

    function subscribersFor(notification) {
      return subscribers.filter(s => s.types.indexOf(notification.notificationType) >= 0);
    }

    function owners(notification, candidates) {
      return candidates.filter(s => {
        if (!s.handle || !s.claim) return false;
        try {
          return !!s.claim(notification);
        } catch (err) {
          log('error', 'claim threw', err);
          return false;
        }
      });
    }

    function runHandlers(list, notification) {
      let ran = 0;
      list.forEach(s => {
        if (!s.handle) return;
        ran++;
        try {
          s.handle(notification);
        } catch (err) {
          log('error', 'handle threw for ' + notification.notificationType, err);
        }
      });
      return ran;
    }

    function runEveryTab(candidates, notification) {
      candidates.forEach(s => {
        if (!s.everyTab) return;
        try {
          s.everyTab(notification);
        } catch (err) {
          log('error', 'everyTab threw for ' + notification.notificationType, err);
        }
      });
    }

    function result(outcome, notification, extra) {
      const r = {
        outcome,
        id: notification && notification.notificationId,
        type: notification && notification.notificationType
      };
      if (extra) Object.keys(extra).forEach(k => { r[k] = extra[k]; });
      log('debug', 'outcome=' + outcome + ' type=' + r.type + ' id=' + r.id, extra);
      outcomeListeners.slice().forEach(fn => {
        try { fn(r, notification); } catch (err) { log('error', 'outcome listener threw', err); }
      });
      return r;
    }

    // Claim order for the user-level path: visible+focused (0) < visible but unfocused < hidden slave
    // < hidden master (last resort), so the prompt/toast appears where the cashier is looking.
    function userClaimDelay() {
      const visible = env.isVisible();
      if (visible && env.hasFocus()) return 0;
      if (visible) return cfg.claimDelayVisibleMs;
      if (env.isMaster()) return cfg.claimDelayMasterMs;
      return cfg.claimDelayHiddenMs;
    }

    /** User-level path: exactly one tab (visible + focused preferred) handles it. */
    function userFlow(notification, candidates) {
      const key = markerKey(notification);
      return env.delay(userClaimDelay()).then(() => {
        if (env.isClaimed(key)) return result(OUTCOMES.lostClaim, notification);
        return env.tryClaim(key).then(won => {
          if (!won) return result(OUTCOMES.lostClaim, notification);

          // Claiming subscribers never run here: their tab is gone or refused the notification, and a
          // foreign tab must not act on an operation it does not own (e.g. someone else's checkout).
          const applicable = candidates.filter(s => s.handle && !s.claim);
          const ran = runHandlers(applicable, notification);
          if (ran > 0) return result(OUTCOMES.handled, notification);

          if (fallbackHandler) {
            try { fallbackHandler(notification); } catch (err) { log('error', 'fallback handler threw', err); }
            return result(OUTCOMES.handledFallback, notification);
          }
          return result(OUTCOMES.noHandler, notification);
        });
      });
    }

    /**
     * Entry point for every notification this tab receives (from the hub or from another tab).
     * @returns {Promise<{outcome: string}>}
     */
    function process(notification) {
      if (!notification || !notification.notificationType) {
        return Promise.resolve(result(OUTCOMES.invalid, notification));
      }
      if (!notification.notificationId) {
        // Sender without an envelope id: cannot coordinate across tabs, still deliver locally.
        notification.notificationId = 'local-' + notification.notificationType + '-' + Math.random().toString(36).slice(2);
        log('warn', 'notification without notificationId', notification.notificationType);
      }
      if (!remember(notification.notificationId)) {
        return Promise.resolve(result(OUTCOMES.duplicate, notification));
      }

      const candidates = subscribersFor(notification);
      runEveryTab(candidates, notification);

      try {
        const owning = owners(notification, candidates);
        if (owning.length > 0) {
          // Owner path: this tab claims at once (another owner tab may race; the marker decides).
          const key = markerKey(notification);
          return env.tryClaim(key).then(won => {
            if (!won) return result(OUTCOMES.lostClaim, notification, { owner: true });
            runHandlers(owning, notification);
            return result(OUTCOMES.handled, notification, { owner: true });
          });
        }

        // No owner in this tab: give the owner tab a moment, then fall back to the user-level path
        // (the owner tab may be gone - navigated away, closed).
        return env.delay(cfg.ownerWaitMs).then(() => {
          if (env.isClaimed(markerKey(notification))) return result(OUTCOMES.lostClaim, notification);
          return userFlow(notification, candidates);
        });
      } catch (err) {
        log('error', 'process failed', err);
        return Promise.resolve(result(OUTCOMES.invalid, notification, { error: String(err) }));
      }
    }

    return {
      process,
      subscribe,
      setFallbackHandler,
      onOutcome,
      settings: cfg,
      subscriberCount: () => subscribers.length
    };
  }

  return {
    OUTCOMES,
    DEFAULT_SETTINGS,
    markerKey,
    createCore
  };
});
